'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

const NexusCoreContext = createContext(null);

/**
 * NexusCoreProvider
 * Manages shared state for Nexus Core (slide-in panel + fullscreen).
 * Only renders children features when user has nexus_core_access.
 */
export function NexusCoreProvider({ children }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Check nexus_core_access from user data
  useEffect(() => {
    const user = BackendAuth.getUser();
    setHasAccess(!!user?.nexus_core_access);

    // Listen for storage changes (login/logout)
    const handleStorage = (e) => {
      if (e.key === 'user_data') {
        try {
          const userData = e.newValue ? JSON.parse(e.newValue) : null;
          setHasAccess(!!userData?.nexus_core_access);
        } catch {
          setHasAccess(false);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen(prev => !prev), []);
  const revokeAccess = useCallback(() => {
    setHasAccess(false);
    setIsOpen(false);
  }, []);

  return (
    <NexusCoreContext.Provider value={{
      hasAccess,
      isOpen,
      openPanel,
      closePanel,
      togglePanel,
      revokeAccess
    }}>
      {children}
    </NexusCoreContext.Provider>
  );
}

export function useNexusCore() {
  const ctx = useContext(NexusCoreContext);
  if (!ctx) {
    throw new Error('useNexusCore must be used within NexusCoreProvider');
  }
  return ctx;
}
